/**
 * Попап після покупки обертів (owner 2026-09-24: «после покупки должно быть
 * красиво, эффектно»).
 *
 * Канон — попап виграшу колеса (`WheelPrizePopup`): max-w-xs, rounded-3xl,
 * хрестик, анімований емодзі, тепле світіння. Від «ефекту» — велике число
 * куплених обертів, що набігає від нуля, і лічильник «тепер у тебе N».
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import { popupActionsCol, popupBtnPrimary } from "@/lib/popupButtons";
import { spinsLabel, spinsWord } from "@/lib/spinsWord";

/** Число, що набігає до цільового (той самий рух, що в подарунку). */
function useCountUp(target: number, ms = 1600): number {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target <= 0) return;
    const start = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      setV(Math.round(target * (1 - Math.pow(1 - p, 4))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

export function WheelPurchasedPopup({
  open,
  spins,
  total,
  onClose,
  onSpin,
}: {
  open: boolean;
  /** Скільки обертів щойно куплено. */
  spins: number;
  /** Скільки обертів на руках тепер (разом із купленими). */
  total: number;
  onClose: () => void;
  onSpin: () => void;
}) {
  const shown = useCountUp(open ? spins : 0);
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-xs overflow-hidden rounded-3xl bg-surface p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(255,161,53,0.34), rgba(255,161,53,0) 70%)",
          }}
        />
        <div
          className="pointer-events-none absolute -bottom-20 -left-16 h-48 w-48 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(254,107,4,0.16), rgba(254,107,4,0) 70%)",
          }}
        />
        <PopupCloseButton onClose={onClose} />

        <div className="relative mb-4 text-center">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="🎉" size={52} />
          </div>
          <div className="mb-1.5 font-display text-[1.125rem] font-bold leading-tight text-ink [text-wrap:balance]">
            Оберти вже твої!
          </div>
          <div className="text-[0.84375rem] leading-relaxed text-ink [text-wrap:balance]">
            Оплата пройшла — колесо чекає на тебе
          </div>

          <div className="mt-3 rounded-2xl bg-primary/[0.07] px-4 py-3 ring-1 ring-primary/20">
            {/* Число й слово одним рядком (owner 2026-09-24). */}
            <div className="flex items-baseline justify-center gap-2">
              <span
                className="font-display text-[2.5rem] font-bold leading-none text-primary tabular-nums"
                style={{ letterSpacing: "-0.02em" }}
              >
                +{shown}
              </span>
              <span className="font-display text-[1.125rem] font-bold text-ink">
                {spinsWord(spins)}
              </span>
            </div>
            <div className="mt-2 inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 font-display text-[0.65625rem] font-bold text-white">
              Тепер у тебе {spinsLabel(Math.max(total, spins))}
            </div>
          </div>
          <div className="mt-3 text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
            Джекпот колеса — рік підписки
          </div>
        </div>

        <div className={"relative " + popupActionsCol}>
          <button type="button" onClick={onSpin} className={popupBtnPrimary}>
            Крутити
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
